import { useCallback, useEffect, useState } from 'react';
import { formatCurrency, formatDate } from '@/lib/format.js';
import { Badge } from '@/components/ui/badge.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { api } from '@/lib/api.js';
import Panel from './Panel.jsx';
import { getDealTransactions, LEDGER_CHANGED_EVENT, recordHowPaid, recordNickelAchCleared, getTrustAccountNames, recordSignedPaperworkReceived, recordSignedLprReceived } from '@/services/deals.service.js';
import { ANSWERS, METHODS, REQUIRED, OPTIONAL, ANSWER_LABELS } from '@/lib/arAnswer.js';
import TransactionCreateSheet from '@/components/sheets/TransactionCreateSheet.jsx';
import VoidTransactionDialog from './VoidTransactionDialog.jsx';
import CommissionRateInline from '@/components/money/CommissionRateInline.jsx';
import ConfirmCancellationInline from './ConfirmCancellationInline.jsx';
import CarrierDocAttach, { ledgerRowName } from './CarrierDocAttach.jsx';
import { useCarrierDocPick, ASKED } from './CarrierDocPick.jsx';
import { isSettledByAnswer, isObligationSettled, financeCompanyName } from '@/lib/arStatus.js';
import { getPaymentHolds, isHoldRefusal } from '@/services/paperwork.service.js';
import { SignedApplicationHold } from '@/components/paperwork/PaymentHoldBlock.jsx';
import SendEndorsementButton from '@/components/paperwork/SendEndorsementButton.jsx';
import SendBeforeClearingButton from '@/components/paperwork/SendBeforeClearingButton.jsx';
import PolicyDocsDropSheet from '@/components/sheets/PolicyDocsDropSheet.jsx';

// Deal-scoped policy-transactions ledger (POLICY-TRANSACTIONS-SPEC.md §3).
// Renders the bind → endorsement → cancellation chain plus the two divergent
// totals (Amount = Σ premium_delta; TAP = annualized premium from the latest
// snapshot). Authoring happens in TransactionCreateSheet.

// The carrier leg is also settled when the finance company paid the carrier (STOP 8 ruling 6, 2026-09-27).
const SETTLED_AP = new Set(['succeeded', 'waived', 'paid_by_finance_company']);

// Row badge — mirrors deriveLedgerStatus on the server. LPR-driven rows
// carry a signature lifecycle in t.status (draft → pending_signature →
// pending_carrier_statement → executed) that outranks the AR/AP legs.
function rowStatus(t) {
  if (t.voided_at) return 'voided';
  if (t.status === 'draft') return 'draft';
  if (t.status === 'pending_signature') return 'awaiting signature';
  if (t.status === 'pending_carrier_statement') return 'awaiting carrier';
  // The carrier's answer is terminal (CLAUDE.md: voided is our mistake, declined is the underwriter's no): its own word, never
  // the PENDING its remit leg would say (paperwork-1 round 2, the Chrome pass).
  if (t.status === 'declined') return 'declined';
  const ar = String(t.ar_payment_status || 'na').toLowerCase();
  const ap = String(t.ap_remit_status || 'na').toLowerCase();
  // Financed (190 §4): its own word, before the settled test, mirroring
  // deriveLedgerStatus. The finance company paid; the badge says so.
  if (ar === 'financed') return 'financed';
  if (isSettledByAnswer(ar) && SETTLED_AP.has(ap)) return 'settled';
  // Deferred: David's deliberate hold — its own word, never 'open' (an open
  // row asks to be collected; a deferred one asks to be remembered).
  if (ar === 'deferred') return 'deferred';
  if (ar === 'pending' || ap === 'pending') return 'pending';
  return 'open';
}
const STATUS_VARIANT = {
  settled: 'success', pending: 'warning', open: 'info', voided: 'muted', declined: 'outline',
  // `draft` IS NOT `muted`. The muted variant is `bg-border/40 text-text-dim
  // line-through`, so it says CANCELLED. Right for `voided` above it, wrong
  // for a draft, which is UNFINISHED. A transaction nobody has executed yet
  // was rendering as one somebody killed. 1 live draft row on the book.
  draft: 'outline', 'awaiting signature': 'warning', 'awaiting carrier': 'warning', deferred: 'warning',
  // financed: its own word (never 'success' as if cash landed, never 'muted' as if killed).
  financed: 'info',
};

const TYPE_LABEL = { bind: 'Bind', endorsement: 'Endorsement', cancellation: 'Cancellation', correction: 'Correction' };

// Friendly leg labels — never expose the raw waived/succeeded enum (broker
// found those confusing). Direction is read off the signed premium_delta.
function legLabels(t) {
  // NULL premium_delta = amount unknown, so there is no direction to read.
  // Defaults to the charge wording rather than inventing a credit.
  const isReturn = t.premium_delta != null && Number(t.premium_delta) < 0;
  return {
    ar: t.ar_payment_status === 'financed' ? `Financed through ${financeCompanyName(t.ar_financed_by)}, loan ${t.ar_payment_ref || '?'}` : (isReturn ? 'Insured credit' : (t.ar_shortfall_absorbed != null ? `Payment (short ${formatCurrency(t.ar_shortfall_absorbed)}, absorbed)` : 'Payment')),
    ap: t.ap_remit_status === 'paid_by_finance_company' ? 'Carrier paid by finance company' : (isReturn ? 'Statement credit' : 'Remitted'),
  };
}

// The carrier's answer to a pending addition endorsement (pilot approval,
// Prompt 164 §3). Approved: the broker types the carrier's additional premium
// (0 is a real answer — approved at no charge) and the txn executes. Declined:
// the carrier's reason is required and the refusal survives as its own
// terminal state — voided means WE made a mistake, declined means the
// underwriter said no.
function EndorsementAnswerInline({ dealId, txn, onAnswered }) {
  const [mode, setMode] = useState(null); // null | 'approve' | 'decline'
  const [premium, setPremium] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const who = (txn.changes?.pilots_added || []).map((p) => p.name).filter(Boolean).join(', ');
  // The carrier's endorsement may come with the approval (paperwork-1 1b): filed through the deal's document door, then handed
  // to the approve door as carrier_document_id. A decline carries none (a refusal has no endorsement).
  const carrierDoc = useCarrierDocPick({ dealId, txnId: txn.id, rowName: ledgerRowName('endorsement', txn.effective_date) });

  async function submit() {
    setBusy(true);
    try {
      if (mode === 'approve') {
        const ap = Number(premium);
        if (premium === '' || !Number.isFinite(ap) || ap < 0) {
          toast.error('Additional premium must be a non-negative number (0 is allowed)'); return;
        }
        const carrierDocumentId = await carrierDoc.documentId();
        if (carrierDocumentId === ASKED) return;
        const res = await api.post(`/api/tracon/transactions/${encodeURIComponent(txn.id)}/endorsement-approved`, { additional_premium: ap, ...(carrierDocumentId ? { carrier_document_id: carrierDocumentId } : {}) });
        if (res?.success === false) throw new Error(res?.error || 'Approve failed');
        if (carrierDocumentId && res?.carrier_document?.id !== carrierDocumentId) throw new Error('The approval was recorded, but the carrier\'s endorsement did not read back linked to this endorsement');
        toast.success(`Carrier approved${who ? ` ${who}` : ''}${ap > 0 ? ` at ${formatCurrency(ap)}` : ' at no charge'}${res?.carrier_document?.name ? `, with ${res.carrier_document.name}` : ''}. Remember to add the pilot to Approved Pilots on the roster.`);
      } else {
        if (!reason.trim()) { toast.error('The carrier’s reason is required. A refusal with no reason is not a record.'); return; }
        const res = await api.post(`/api/tracon/transactions/${encodeURIComponent(txn.id)}/endorsement-declined`, { reason: reason.trim() });
        if (res?.success === false) throw new Error(res?.error || 'Decline failed');
        toast.success(`Carrier declined${who ? ` ${who}` : ''}. The refusal is on the ledger with the reason.`);
      }
      onAnswered?.();
    } catch (err) {
      toast.error(err?.body?.error || err.message || 'Recording the answer failed');
    } finally {
      setBusy(false);
    }
  }

  if (!mode) {
    return (
      <div className="mt-1.5 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => setMode('approve')}>Carrier approved{who ? ` (${who})` : ''}</Button>
        <Button size="sm" variant="ghost" className="text-danger" onClick={() => setMode('decline')}>Carrier declined</Button>
      </div>
    );
  }
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2">
      {mode === 'approve' ? (
        <Input type="number" min="0" step="0.01" value={premium} onChange={(e) => setPremium(e.target.value)}
          placeholder="Additional premium (0 if none)" className="h-8 w-52 text-sm" />
      ) : (
        <Input value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Carrier's reason for declining" className="h-8 w-64 text-sm" />
      )}
      <Button size="sm" onClick={submit} disabled={busy}>{busy ? 'Recording…' : mode === 'approve' ? 'Record approval' : 'Record decline'}</Button>
      <Button size="sm" variant="ghost" onClick={() => setMode(null)} disabled={busy}>Back</Button>
      {mode === 'approve' ? carrierDoc.field : null}
    </div>
  );
}

// A Nickel ACH whose clearing event never settled the row. The day is typed from
// Nickel's "Processed ACH Payment" email, never defaulted. Read back after.
function NickelAchClearedInline({ dealId, txn, onRecorded }) {
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit() {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) { toast.error('Enter the day from the Nickel "Processed ACH Payment" email.'); return; }
    setBusy(true);
    try {
      const res = await recordNickelAchCleared(txn.id, day);
      if (res?.success === false) throw new Error(res?.error || 'Could not settle the ACH');
      let row = null;
      try {
        const back = await getDealTransactions(dealId, { includeVoided: true });
        row = (back?.transactions || []).find((t) => t.id === txn.id) || null;
      } catch (_) { row = null; }
      if (!row) toast.info('Recorded, but the ledger could not be re-read to confirm. Refresh to check.');
      else if (row.ar_payment_status === 'succeeded') toast.success(`Ledger now reads succeeded${row.ar_paid_on ? `, ${row.ar_paid_on}` : ''}.`);
      else toast.error(`The ledger still reads ${row.ar_payment_status}. The ACH did not settle.`);
      (res?.partial || []).forEach((p) => toast.info(p));
      setOpen(false);
      onRecorded?.();
    } catch (err) {
      toast.error(err?.body?.error || err.message || 'Could not settle the ACH');
    } finally {
      setBusy(false);
    }
  }
  if (!open) {
    return (
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => setOpen(true)}>Nickel ACH cleared</Button>
        <span className="text-[11px] text-text-muted">Nickel {txn.ar_payment_ref}, {formatCurrency(txn.ar_paid_amount)}, still pending.</span>
      </div>
    );
  }
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2">
      <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="h-8 w-40 text-xs" title="Day it cleared (Nickel Processed email)" />
      <Button size="sm" onClick={submit} disabled={busy || !day}>{busy ? 'Recording…' : 'Record cleared'}</Button>
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
    </div>
  );
}

// SIGNED PAPERWORK RECEIVED (Q4, 2026-10-28; endorsements too, answers round 2 Q3-C): a live bind or endorsement with no
// signature on the ledger. The broker states the day the client's signed paperwork arrived (never defaulted); the server
// records it through the one signature writer, and a paid line then closes on its own row (paperwork and money). The
// ledger is READ BACK after: the toast says what the store holds.
// paperwork-1 L2-2 (D9, A13): on a bind the server asks the signed application window first. Hold: refused (409
// SIGNED_APPLICATION_NOT_FILED), nothing written, the hold shown here with its button. Notify: recorded, and each warning
// the server answers is shown (toast.info), never only on the console.
function SignedPaperworkReceivedInline({ dealId, txn, onRecorded, onHeld, held = false }) {
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit() {
    if (!day) return;
    setBusy(true);
    try {
      const res = await recordSignedPaperworkReceived(txn.id, day);
      if (res?.success === false) throw new Error(res?.error || 'Could not record the signed paperwork');
      let row = null;
      try {
        const back = await getDealTransactions(dealId, { includeVoided: true });
        row = (back?.transactions || []).find((t) => t.id === txn.id) || null;
      } catch (_) { row = null; }
      if (!row) toast.info('Recorded, but the ledger could not be re-read to confirm. Refresh to check.');
      else if (row.signed_at) toast.success(res?.closed_on ? `Signed paperwork recorded. The ${txn.type} is closed (paperwork and money), ${res.closed_on}.` : `Signed paperwork recorded. The ${txn.type} still waits for the money.`);
      else toast.error('The ledger still shows no signature. It did not land.');
      (res?.partial || []).forEach((p) => toast.info(p));
      (res?.warnings || []).forEach((w) => toast.info(w));
      setOpen(false);
      onRecorded?.();
    } catch (err) {
      if (isHoldRefusal(err)) { setOpen(false); onHeld?.(err.body); return; } // the hold block on the row says why, with its button
      toast.error(err?.body?.error || err.message || 'Could not record the signed paperwork');
    } finally {
      setBusy(false);
    }
  }
  if (!open) {
    return (
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} disabled={held} title={held ? 'Held until the signed application is filed (below)' : undefined}>Signed paperwork received</Button>
        <span className="text-[11px] text-text-muted">{held ? 'Held: file the signed application first (below).' : `No signature on the ledger for this ${txn.type}.`}</span>
      </div>
    );
  }
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2">
      <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="h-8 w-40 text-xs" title="Day the signed paperwork arrived" />
      <Button size="sm" onClick={submit} disabled={busy || !day}>{busy ? 'Recording...' : 'Record received'}</Button>
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
    </div>
  );
}

// SIGNED LPR RECEIVED (deals-door-1 round 3, R2k-Q7): a live cancellation with no signed LPR on the ledger (it arrived on paper
// or by email). The broker states the day it arrived (never defaulted); the server records it through the LPR fact's one
// writer and, when it is the second document, moves the deal (a future-dated cancellation waits for its day). The ledger is
// READ BACK after; the toast says what the store holds and the cancellation's state as the server reads it.
function SignedLprReceivedInline({ dealId, txn, onRecorded }) {
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState('');
  const [busy, setBusy] = useState(false);
  async function submit() {
    if (!day) return;
    setBusy(true);
    try {
      const res = await recordSignedLprReceived(txn.id, day);
      if (res?.success === false) throw new Error(res?.error || 'Could not record the signed LPR');
      let row = null;
      try {
        const back = await getDealTransactions(dealId, { includeVoided: true });
        row = (back?.transactions || []).find((t) => t.id === txn.id) || null;
      } catch (_) { row = null; }
      if (!row) toast.info('Recorded, but the ledger could not be re-read to confirm. Refresh to check.');
      else if (row.signed_at) toast.success(res?.cancellation?.state === 'cancelled' ? 'Signed LPR recorded. Both documents are on file; the deal is cancelled.' : `Signed LPR recorded.${res?.cancellation?.sentence ? ` ${res.cancellation.sentence}.` : ''}`);
      else toast.error('The ledger still shows no signed LPR. It did not land.');
      setOpen(false);
      onRecorded?.();
    } catch (err) {
      toast.error(err?.body?.error || err.message || 'Could not record the signed LPR');
    } finally {
      setBusy(false);
    }
  }
  if (!open) {
    return (
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => setOpen(true)}>Signed LPR received</Button>
        <span className="text-[11px] text-text-muted">No signed LPR on the ledger for this cancellation.</span>
      </div>
    );
  }
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2">
      <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="h-8 w-40 text-xs" title="Day the signed LPR arrived" />
      <Button size="sm" onClick={submit} disabled={busy || !day}>{busy ? 'Recording...' : 'Record received'}</Button>
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
    </div>
  );
}

// 190 §4 and ADDENDUM §D: RECORD HOW THIS WAS PAID. One handle, six answers
// (paid outside Nickel, collected elsewhere, deferred, financed, financed into
// trust, void), each through the door that owns it on the server; the form
// only shows the fields the chosen answer needs. Nothing is defaulted. After
// posting, the ledger is READ BACK through the same endpoint this panel
// renders from: the toast reports what the store now holds, never the
// response body.
const WORD_AFTER = { paid_outside_nickel: 'succeeded', paid_short_absorbed: 'succeeded', collected_elsewhere: 'waived', deferred: 'deferred', financed: 'financed', financed_acceleration: 'succeeded' };
function RecordHowPaidInline({ dealId, txn, trustAccounts, onRecorded }) {
  const [formOpen, setFormOpen] = useState(false);
  const [answer, setAnswer] = useState('');
  const [f, setF] = useState({ amount: '', date: '', method: '', reference: '', trust_account: '', finance_company: '', reason: '', shortfall: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((prev) => ({ ...prev, [k]: e.target.value }));
  const pf = txn.carrier_pf_funds_via_agency;
  // The two financing answers are mutually exclusive by paper: hidden with the reason, never a 409 on click.
  const options = ANSWERS.filter((a) => a !== 'not_sure').filter((a) => !(a === 'financed' && pf === true)).filter((a) => !(a === 'financed_acceleration' && pf !== true));
  const fields = answer ? [...(REQUIRED[answer] || []), ...(OPTIONAL[answer] || [])] : [];
  const missing = answer ? (REQUIRED[answer] || []).filter((k) => !String(f[k] || '').trim()) : [];

  async function submit() {
    if (!answer) { toast.error('Pick how this was paid first.'); return; }
    if (missing.length) { toast.error(`${ANSWER_LABELS[answer]} needs: ${missing.join(', ')}.`); return; }
    if (f.date && !/^\d{4}-\d{2}-\d{2}$/.test(f.date)) { toast.error('The date must be YYYY-MM-DD.'); return; }
    setBusy(true);
    try {
      const body = { answer };
      for (const k of fields) if (String(f[k] || '').trim()) body[k] = String(f[k]).trim();
      const res = await recordHowPaid(txn.id, body);
      if (res?.success === false) throw new Error(res?.error || 'Could not record the answer');
      // Verification is a read, not an echo.
      let row = null;
      try {
        const back = await getDealTransactions(dealId, { includeVoided: true });
        row = (back?.transactions || []).find((t) => t.id === txn.id) || null;
      } catch (_) { row = null; }
      if (!row) toast.info('Recorded, but the ledger could not be re-read to confirm. Refresh to check.');
      else if (answer === 'void') toast[row.voided_at ? 'success' : 'error'](row.voided_at ? 'Ledger now shows this bind voided.' : 'The ledger still shows this bind live. The void did not land.');
      else if (row.ar_payment_status === WORD_AFTER[answer]) toast.success(`Ledger now reads ${row.ar_payment_status}${row.ar_payment_status === 'financed' ? ` through ${financeCompanyName(row.ar_financed_by)}, loan ${row.ar_payment_ref}` : ''}${row.ar_paid_on ? `, ${row.ar_paid_on}` : ''}.`);
      else toast.error(`The ledger still reads ${row.ar_payment_status}. The answer did not land.`);
      setFormOpen(false);
      onRecorded?.();
    } catch (err) {
      toast.error(err?.body?.error || err.message || 'Could not record the answer');
    } finally {
      setBusy(false);
    }
  }

  if (!formOpen) {
    return (
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => setFormOpen(true)}>Record how this was paid</Button>
        {pf === true ? <span className="text-[11px] text-text-muted">{txn.carrier_name || 'This carrier'} does not take finance-company money: a financed deal here is a trust receipt.</span> : null}
        {pf == null ? <span className="text-[11px] text-text-muted">Carrier unknown on this row: the financing answers need it stated first.</span> : null}
      </div>
    );
  }
  return (
    <div className="mt-1.5 flex flex-col gap-2">
      <select value={answer} onChange={(e) => setAnswer(e.target.value)} className="h-8 max-w-md rounded-card border border-border bg-surface px-2 text-xs text-text">
        <option value="">How was this paid?</option>
        {options.map((a) => <option key={a} value={a}>{ANSWER_LABELS[a]}</option>)}
      </select>
      {answer ? (
        <div className="flex flex-wrap items-center gap-2">
          {fields.includes('amount') ? <Input type="number" min="0" step="0.01" value={f.amount} onChange={set('amount')} placeholder="Amount $" className="h-8 w-32 text-xs" /> : null}
          {fields.includes('date') ? <Input type="date" value={f.date} onChange={set('date')} className="h-8 w-40 text-xs" title={answer === 'deferred' ? 'Collect on (optional)' : 'Date'} /> : null}
          {fields.includes('method') ? (
            <select value={f.method} onChange={set('method')} className="h-8 rounded-card border border-border bg-surface px-2 text-xs text-text">
              <option value="">Method</option>
              {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          ) : null}
          {fields.includes('reference') ? <Input value={f.reference} onChange={set('reference')} placeholder={answer.startsWith('financed') ? 'Loan or program reference' : 'Check number, wire or payment ref'} className="h-8 w-44 text-xs" /> : null}
          {fields.includes('trust_account') ? (
            <select value={f.trust_account} onChange={set('trust_account')} className="h-8 rounded-card border border-border bg-surface px-2 text-xs text-text">
              <option value="">Trust account</option>
              {(trustAccounts || []).map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          ) : null}
          {fields.includes('shortfall') ? <Input type="number" min="0" step="0.01" value={f.shortfall} onChange={set('shortfall')} placeholder="Short by $ (absorbed)" className="h-8 w-40 text-xs" /> : null}
          {fields.includes('finance_company') ? <Input value={f.finance_company} onChange={set('finance_company')} placeholder="Finance company (e.g. Ascend)" className="h-8 w-44 text-xs" /> : null}
          {fields.includes('reason') ? <Input value={f.reason} onChange={set('reason')} placeholder="Reason" className="h-8 w-56 text-xs" /> : null}
        </div>
      ) : null}
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={submit} disabled={busy || !answer}>{busy ? 'Recording…' : 'Record'}</Button>
        <Button size="sm" variant="ghost" onClick={() => setFormOpen(false)} disabled={busy}>Cancel</Button>
      </div>
    </div>
  );
}

// Inline confirm for a signed LPR removal endorsement. The broker types the
// carrier's actual return premium (TRACON never computes one); confirming
// flips the txn to executed and runs the roster removal through applyRoster.
function ConfirmEndorsementInline({ dealId, txn, onConfirmed }) {
  const [formOpen, setFormOpen] = useState(false);
  const [returnPremium, setReturnPremium] = useState('');
  const [busy, setBusy] = useState(false);
  const tails = txn.changes?.aircraft_removed || [];
  // The carrier's endorsement may come with the confirmation (paperwork-1 1b): filed, then handed over as carrier_document_id.
  const carrierDoc = useCarrierDocPick({ dealId, txnId: txn.id, rowName: ledgerRowName('endorsement', txn.effective_date) });

  async function confirm() {
    const rp = Number(returnPremium);
    if (returnPremium === '' || !Number.isFinite(rp) || rp < 0) {
      toast.error('Return premium must be a non-negative number (0 is allowed)');
      return;
    }
    setBusy(true);
    try {
      const carrierDocumentId = await carrierDoc.documentId();
      if (carrierDocumentId === ASKED) return;
      const res = await api.post(
        `/api/lpr/deal/${encodeURIComponent(dealId)}/transaction/${encodeURIComponent(txn.id)}/confirm-endorsement`,
        { return_premium: rp, ...(carrierDocumentId ? { carrier_document_id: carrierDocumentId } : {}) }
      );
      if (res?.success === false) throw new Error(res?.error || 'Confirm failed');
      if (carrierDocumentId && res?.carrier_document?.id !== carrierDocumentId) throw new Error('The endorsement was confirmed, but the carrier\'s endorsement did not read back linked to it');
      toast.success(`Endorsement confirmed: ${tails.join(', ')} removed from the roster${res?.carrier_document?.name ? `, with ${res.carrier_document.name}` : ''}`);
      // Third roster-apply door: same warnings array as RosterSheet /
      // QuoteDiffSheet. A warning routed to the console (or dropped) is a
      // warning that was not issued (David 2026-08-12).
      for (const w of res?.warnings || []) toast.info(w);
      onConfirmed?.();
    } catch (err) {
      toast.error(err?.body?.error || err.message || 'Confirm failed');
    } finally {
      setBusy(false);
    }
  }

  if (!formOpen) {
    return (
      <div className="mt-1.5">
        <Button size="sm" variant="outline" onClick={() => setFormOpen(true)}>
          Confirm carrier endorsement ({tails.join(', ')})
        </Button>
      </div>
    );
  }
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2">
      <Input
        type="number"
        min="0"
        step="0.01"
        value={returnPremium}
        onChange={(e) => setReturnPremium(e.target.value)}
        placeholder="Return premium $"
        className="h-8 w-40 text-xs"
      />
      <Button size="sm" onClick={confirm} disabled={busy}>
        {busy ? 'Confirming…' : 'Confirm + update roster'}
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setFormOpen(false)} disabled={busy}>
        Cancel
      </Button>
      {carrierDoc.field}
    </div>
  );
}

// clientName (paperwork-1 L2-2): the named insured as a person reads it, for "Send endorsement to <client>".
export default function TransactionHistoryPanel({ dealId, clientName = null }) {
  const [data, setData] = useState(null);
  // The paperwork holds (paperwork-1 D9, A13, L2-2): the server's preview, read with the ledger; the bind row shows the signed
  // application window's state (hold or notify, or flagged once recorded) with its button. A failed read shows nothing here
  // (the server still decides at the door) and says so on the bind row.
  const [holds, setHolds] = useState(null);
  const [holdsFailed, setHoldsFailed] = useState(false);
  const [heldBody, setHeldBody] = useState(null);
  const [appDropOpen, setAppDropOpen] = useState(false);
  const [error, setError] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [voidTxn, setVoidTxn] = useState(null);
  // Trust account names for the how-paid answers that land cash. A failed read
  // is an empty list, and the select says so rather than inventing one.
  const [trustAccounts, setTrustAccounts] = useState([]);
  useEffect(() => { getTrustAccountNames().then(setTrustAccounts).catch(() => setTrustAccounts([])); }, []);

  const refresh = useCallback(() => {
    if (!dealId) return;
    getDealTransactions(dealId, { includeVoided: true })
      .then((r) => { setData(r || null); setError(null); })
      .catch((e) => setError(e));
    getPaymentHolds(dealId).then((r) => { setHolds(r || null); setHoldsFailed(false); }).catch(() => { setHolds(null); setHoldsFailed(true); });
  }, [dealId]);

  useEffect(() => { refresh(); }, [refresh]);
  // A ledger change made elsewhere on the page (the cancellation note's buttons) re-reads this panel too.
  useEffect(() => {
    const on = (e) => { if (String(e?.detail?.dealId) === String(dealId)) refresh(); };
    window.addEventListener(LEDGER_CHANGED_EVENT, on);
    return () => window.removeEventListener(LEDGER_CHANGED_EVENT, on);
  }, [dealId, refresh]);

  if (!dealId) return null;

  const txns = data?.transactions || [];
  const derived = data?.derived || null;
  // Each row's carrier document came with the ledger read (paperwork-1 1b); false says that read failed: "could not check".
  const carrierDocsChecked = data?.carrier_documents_checked === true;
  const tap = data?.tap ?? null;
  const hasBaseline = txns.some((t) => !t.voided_at);

  return (
    <>
      <Panel
        title="Transaction history"
        count={txns.length}
        empty={
          error
            ? `Couldn't load transactions: ${error.message || 'unknown'}`
            : !data
              ? null
              : txns.length === 0
                ? 'No transactions yet — establish the policy baseline to start the ledger.'
                : null
        }
        footer={
          <div className="flex items-center justify-between">
            <div className="font-mono text-[11px] text-text-dim">
              {derived ? (
                <>Amount {formatCurrency(derived.amount)}{tap != null ? <> · TAP {formatCurrency(tap)}</> : <> · TAP —</>}</>
              ) : (
                <>Amount — · TAP —</>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={() => setSheetOpen(true)}>
              {hasBaseline ? '+ Endorsement' : 'Establish baseline'}
            </Button>
          </div>
        }
      >
        {txns.length > 0 ? (
          <ul className="divide-y divide-border">
            {txns.map((t) => {
              const st = rowStatus(t);
              const labels = legLabels(t);
              // Unknown is not zero (CC PROMPT 56 item 1). A signed
              // cancellation whose carrier statement has not landed renders
              // as pending, never as $0.00, which the broker reads as "the
              // carrier returned nothing".
              const deltaUnknown = t.premium_delta == null;
              const delta = deltaUnknown ? 0 : Number(t.premium_delta);
              return (
                <li key={t.id} className={`py-2 ${t.voided_at ? 'opacity-50' : ''}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-baseline gap-2 text-xs">
                        <span className="font-medium text-text">{TYPE_LABEL[t.type] || t.type}</span>
                        <span className="text-text-dim">{formatDate(t.effective_date)}</span>
                        <Badge variant={STATUS_VARIANT[st] || 'outline'} className="text-[9px]">
                          {st.toUpperCase()}
                        </Badge>
                      </div>
                      {t.description ? (
                        <div className="mt-0.5 truncate text-sm text-text">{t.description}</div>
                      ) : null}
                      {/* AR/AP legs — friendly labels, magnitudes */}
                      {(t.ar_payment_status && t.ar_payment_status !== 'na') ||
                       (t.ap_remit_status && t.ap_remit_status !== 'na') ? (
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 font-mono text-[10px] text-text-dim">
                          {t.ar_payment_status && t.ar_payment_status !== 'na' ? (
                            <span>{labels.ar}{t.ar_payment_status === 'financed' ? '' : <> {formatCurrency(t.ar_paid_amount)}</>}</span>
                          ) : null}
                          {t.ap_remit_status && t.ap_remit_status !== 'na' ? (
                            <span>{labels.ap}{t.ap_remit_amount != null ? ` ${formatCurrency(t.ap_remit_amount)}` : ''}</span>
                          ) : null}
                        </div>
                      ) : null}
                      {/* Commission: what the row states, and the rate door's
                          handle on any live row with a premium, paid or not
                          (a deferred or financed bind never reaches the sweep
                          page, which was the door's only sender). */}
                      {!t.voided_at && t.type !== 'correction' && t.premium_delta != null ? (
                        <CommissionRateInline txn={t} onSaved={refresh} />
                      ) : null}
                      {/* LPR scoped endorsement, signed and awaiting the
                          carrier statement: confirm inline. Entering the
                          return premium executes the txn AND applies the
                          roster removal (spec piece 4 — roster changes go
                          through after endorsement). */}
                      {t.type === 'endorsement'
                        && t.status === 'pending_carrier_statement'
                        && !t.voided_at
                        && (t.changes?.aircraft_removed?.length > 0) ? (
                        <ConfirmEndorsementInline dealId={dealId} txn={t} onConfirmed={refresh} />
                      ) : null}
                      {/* Whole-policy cancellation, signed and awaiting the
                          carrier's return premium. Separate door from the
                          endorsement one above: no roster moves here. */}
                      {t.type === 'cancellation'
                        && t.status === 'pending_carrier_statement'
                        && !t.voided_at ? (
                        <ConfirmCancellationInline dealId={dealId} txn={t} label={`Record carrier return premium for ${ledgerRowName(t.type, t.effective_date)}`} onConfirmed={refresh} />
                      ) : null}
                      {/* The carrier's own document on a cancellation or endorsement row (David 2026-10-29): filed with
                          the deal's documents, linked to this row, opened from here. */}
                      {/* Q15 (David 2026-10-31): a declined row never offers "Attach carrier's document" (he enters an endorsement only
                          once it is issued); a document already on it is still shown. */}
                      {(t.type === 'cancellation' || t.type === 'endorsement') && !t.voided_at && (t.status !== 'declined' || t.carrier_document) ? (
                        <CarrierDocAttach dealId={dealId} txnId={t.id} document={t.carrier_document || null} unchecked={!carrierDocsChecked} rowName={ledgerRowName(t.type, t.effective_date)} onAttached={refresh} />
                      ) : null}
                      {/* 190 §4 and ADDENDUM §D: an unsettled live bind can say how
                          it was actually paid. The financing answers are shown or
                          hidden by the row's paper, with the reason. */}
                      {t.type === 'bind'
                        && !t.voided_at
                        && t.ar_payment_status === 'pending'
                        && t.ar_payment_method === 'ach'
                        && t.ar_payment_ref ? (
                        <NickelAchClearedInline dealId={dealId} txn={t} onRecorded={refresh} />
                      ) : null}
                      {/* N12 (David 2026-11-01): never on a declined row (the carrier said no; the server refuses it too). */}
                      {(t.type === 'bind' || t.type === 'endorsement')
                        && !t.voided_at
                        && t.status !== 'declined'
                        && !t.signed_at ? (
                        <SignedPaperworkReceivedInline dealId={dealId} txn={t} onRecorded={() => { setHeldBody(null); refresh(); }} onHeld={(b) => { setHeldBody(b); refresh(); }}
                          held={t.type === 'bind' && (!!heldBody || holds?.signed_paperwork?.would_hold === true)} />
                      ) : null}
                      {t.type === 'bind' && !t.voided_at ? (
                        <div className="mt-1.5" data-bind-paperwork-hold={t.id}>
                          {holdsFailed && !t.signed_at ? <p className="text-[11px] text-text-muted">Could not check the signed application for this bind; the server still checks it when you record.</p> : null}
                          <SignedApplicationHold finding={heldBody?.hold || (holds?.signed_paperwork && (!t.signed_at || holds.signed_paperwork.state === 'out') ? holds.signed_paperwork : null)} mode={heldBody?.mode || holds?.mode} door={t.signed_at ? 'paperwork_recorded' : 'paperwork'}
                            dealId={dealId} policyLabel={holds?.signed_application?.policy?.label || 'this policy'} onDrop={() => setAppDropOpen(true)} onChanged={refresh} />
                        </div>
                      ) : null}
                      {/* paperwork-1 D11 (L2-2): every live endorsement row, whatever its money, gets the click (L1a's stub; L3 fills it).
                          Round 2 (Q24): while its Nickel payment is in flight (payment_processing, the server's one derivation) the
                          row offers "Payment processing: send now" INSTEAD: it takes the automatic send's claim, so the clearing
                          sends nothing again, where the plain click would let the clearing send a second copy. */}
                      {t.type === 'endorsement' && !t.voided_at && t.status !== 'declined' && t.status !== 'voided' && !t.payment_processing ? (
                        <div className="mt-1.5"><SendEndorsementButton dealId={dealId} transactionId={t.id} clientName={clientName || 'the client'} /></div>
                      ) : null}
                      {t.payment_processing ? (
                        <div className="mt-1.5" data-payment-processing={t.id}><SendBeforeClearingButton dealId={dealId} transactionId={t.id} clientName={clientName || 'the client'} onSent={refresh} /></div>
                      ) : null}
                      {t.type === 'cancellation'
                        && !t.voided_at
                        && t.status !== 'voided'
                        && !t.signed_at ? (
                        <SignedLprReceivedInline dealId={dealId} txn={t} onRecorded={refresh} />
                      ) : null}
                      {t.type === 'bind'
                        && !t.voided_at
                        && t.status !== 'draft'
                        && !isObligationSettled(t.ar_payment_status) ? (
                        <RecordHowPaidInline dealId={dealId} txn={t} trustAccounts={trustAccounts} onRecorded={refresh} />
                      ) : null}
                      {/* A pilot-approval (or other addition) endorsement
                          awaiting the carrier's answer. These are the ONLY
                          ways out of Seeking approval — the answer doors were
                          built in prompt 156 with no route and no sender (the
                          incident-response shape, found by the 164 sweep). */}
                      {t.type === 'endorsement'
                        && t.status === 'pending_carrier_statement'
                        && !t.voided_at
                        && !(t.changes?.aircraft_removed?.length > 0) ? (
                        <EndorsementAnswerInline dealId={dealId} txn={t} onAnswered={refresh} />
                      ) : null}
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <div className={`font-mono text-sm ${deltaUnknown ? 'text-text-dim' : delta < 0 ? 'text-success' : 'text-text'}`}>
                        {deltaUnknown
                          // A declined request was never priced (premium_delta stays NULL): the carrier answered, nothing is pending.
                          ? (t.status === 'declined' ? 'Declined: never priced' : 'Pending carrier')
                          : <>{delta < 0 ? '−' : ''}{formatCurrency(Math.abs(delta))}</>}
                      </div>
                      {!t.voided_at ? (
                        <button
                          type="button"
                          onClick={() => setVoidTxn(t)}
                          className="text-[10px] text-text-dim underline-offset-2 hover:text-text hover:underline"
                        >
                          Void…
                        </button>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
      </Panel>

      <TransactionCreateSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        dealId={dealId}
        hasBaseline={hasBaseline}
        latestSnapshot={derived?.snapshot || null}
        priorTransactionId={txns.filter((t) => !t.voided_at).slice(-1)[0]?.id || null}
        onCreated={() => { setSheetOpen(false); refresh(); }}
      />

      <PolicyDocsDropSheet dealId={dealId} open={appDropOpen} onOpenChange={setAppDropOpen} initialKind="application" onSuccess={() => { setAppDropOpen(false); setHeldBody(null); refresh(); }} />

      <VoidTransactionDialog
        open={!!voidTxn}
        onOpenChange={(o) => { if (!o) setVoidTxn(null); }}
        txn={voidTxn}
        onVoided={refresh}
      />
    </>
  );
}
